import React, { Component, ErrorInfo, ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, RefreshCw, MessageSquare, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class AiErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("AI Platform Boundary caught error:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleStartNewChat = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/ai";
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl text-center space-y-4">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
              <AlertCircle size={30} />
            </div>

            <div className="space-y-1">
              <h2 className="text-xl font-bold tracking-tight">
                {this.props.fallbackTitle || "Something went wrong in this section"}
              </h2>
              <p className="text-xs text-muted-foreground">
                The AI workspace encountered an issue, but your session and other tabs are completely intact.
              </p>
            </div>

            {this.state.error?.message && (
              <div className="rounded-xl bg-secondary/50 p-3 text-left">
                <p className="text-[11px] font-mono text-muted-foreground break-words line-clamp-3">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button
                variant="outline"
                onClick={this.handleReset}
                className="gap-2 rounded-xl text-xs h-10"
              >
                <RefreshCw size={14} /> Reload Section
              </Button>
              <Button
                onClick={this.handleStartNewChat}
                className="gap-2 rounded-xl text-xs h-10 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <MessageSquare size={14} /> New Chat
              </Button>
            </div>

            <div className="pt-2 border-t border-border flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <Link to="/ai" className="hover:text-foreground flex items-center gap-1">
                <Sparkles size={12} className="text-primary" /> AI Workspace
              </Link>
              <span>•</span>
              <Link to="/ai/pricing" className="hover:text-foreground">
                Pricing
              </Link>
              <span>•</span>
              <Link to="/ai/docs" className="hover:text-foreground">
                Documentation
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
